






































































import Croppa from 'vue-croppa';
import VueDraggableResizable from 'vue-draggable-resizable';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ImagePositionHandler from './ImagePositionHandler';
import { ImageSize, ImageUnionTypes } from './components/constants';
import { uploadCroppedFile } from '../../utils/imageUploader';
import { ICroppedImageMetadata } from '../../types';
import { DEFAULT_IMAGE_HEIGHT_IN_PERCENTS, ONE_HUNDRED, TWO } from '../../constants';
import ImageControlsToolbar from './components/ImageControlsToolbar.vue';

const IMAGE_QUALITY = 2;
const CHANGE_CROPPER_HEIGHT = 'change-cropper-height';
const MAX_WITH_FOR_RESIZE_IN_PERCENTS = 80;

@Component({
  components: {
    Croppa: Croppa.component,
    VueDraggableResizable,
    ImageControlsToolbar
  }
})
export default class ImageCropper extends Vue {
  @Prop({ default: null }) staticCanvasWidth!: number | null;

  @Prop({ default: null }) staticCanvasHeight!: number | null;

  @Prop({ default: 0 }) staticMaxWidthForResize!: number;

  @Prop({ default: true }) withResizer!: boolean;

  @Prop({ default: true }) withResizeOption!: boolean;

  @Prop({ default: true }) showMediumSizeControl!: boolean;

  @Prop({ default: true }) showLargeSizeControl!: boolean;

  @Prop({ default: false }) preventWhiteSpace!: boolean;

  @Prop({ default: false }) toolBarToTheRight!: boolean;

  @Prop({ default: 'oneImage' }) currentViewMode!: ImageUnionTypes;

  @Prop() cropperImage!: string;

  @Prop({ default: () => ['bm'] }) resizeConfig?: string[];

  @Prop() isImageLoaded?: boolean;

  @Prop() isImageInitialized?: boolean;

  @Prop() cropperWrapperClasses!: string;

  @Prop({ default: '0' }) fileSize!: string;

  @Prop({ default: 0 }) progress!: number;

  @Prop({ default: ImageSize.custom }) initialImageSize?: ImageSize;

  @Prop() imageMetadata!: ICroppedImageMetadata;

  cropperMetadata: ICroppedImageMetadata = this.imageMetadata;

  cropperImageUrl!: string;

  $refs!: {
    imageContainer: HTMLDivElement;
    parentContainer: HTMLDivElement;
    imageBlockContainer: HTMLDivElement;
  };

  croppa: typeof Croppa.component = {};

  canvasWidth = 0;

  canvasHeight = 0;

  parentContainerWidth = 0;

  imageWrapperWidthInPercents = ONE_HUNDRED;

  imageWrapperHeightInPercents = DEFAULT_IMAGE_HEIGHT_IN_PERCENTS;

  imageSize = ImageSize.custom;

  imagePositionHandler!: ImagePositionHandler;

  isGifAnimation = false;

  @Watch('cropperImage')
  updateCropperImageUrl(url: string) {
    this.checkImageExtension(url);
    this.croppa.refresh();
  }

  @Watch('imageMetadata')
  onCropperMetadataUpdate(newMetadata: ICroppedImageMetadata) {
    this.cropperMetadata = newMetadata;
    this.initImageProportions();
  }

  get computedCanvasWidth() {
    return this.staticCanvasWidth || this.canvasWidth;
  }

  get computedCanvasHeight() {
    return this.staticCanvasHeight || this.canvasHeight;
  }

  get canvasScale() {
    return this.metadata.w ? this.computedCanvasWidth / this.metadata.w : 1;
  }

  get maxWidthForResize() {
    return this.staticMaxWidthForResize || MAX_WITH_FOR_RESIZE_IN_PERCENTS * this.parentContainerWidth / ONE_HUNDRED;
  }

  get metadata() {
    return this.cropperMetadata;
  }

  set metadata(data) {
    this.cropperMetadata = { ...data };
  }

  created() {
    this.cropperImageUrl = this.cropperImage;
    this.imageSize = this.initialImageSize || ImageSize.custom;
    this.checkImageExtension(this.cropperImage);

    const { width = 0 } = {};
    this.imageWrapperWidthInPercents = width;
  }

  onInit() {
    this.parentContainerWidth = this.$refs.imageBlockContainer.clientWidth;

    if (this.isImageInitialized) {
      return;
    }

    this.croppa.initialSize = 'contain';
    this.$emit('init');
  }

  onLoadingEnd() {
    this.croppa.quality = IMAGE_QUALITY;

    this.initImageProportions();
    this.$emit('loading-end', { imagePositionHandler: this.imagePositionHandler });
  }

  onInitialImageLoaded() {
    this.setInitImageSize();
    this.$emit('initial-image-loaded');
  }

  onResizing(x: number, y: number, width: number, height: number) {
    this.canvasHeight = height;
    this.canvasWidth = width;
    this.imageSize = ImageSize.custom;
    this.onResizeStop(0, 0, width);

    this.$emit('resizing', { x, y, width, height });
  }

  onResizeStop(x: number, y: number, width: number) {
    this.setWidthInPercents(width);
    this.setHeightInPercents();
    this.$emit(CHANGE_CROPPER_HEIGHT, this.canvasHeight, this.imageWrapperHeightInPercents);

    this.$emit('resizing:stop', { x, y, width });
  }

  async resizeToolbarChoose(imageSize: ImageSize) {
    await this.updateImageSize(imageSize);
    this.$emit('update-image-size', imageSize);
  }

  initImageProportions() {
    this.imagePositionHandler = new ImagePositionHandler(this.croppa);
    this.setCanvasSize();

    if (!this.metadata.scale) {
      if (this.croppa.initialSize === 'natural') {
        this.metadata.defaultScale = this.croppa.scaleRatio * IMAGE_QUALITY;

        this.croppa.scaleRatio = this.croppa.scaleRatio * IMAGE_QUALITY;
        this.imagePositionHandler.toCenter();
        return;
      }

      this.metadata.defaultScale = this.croppa.scaleRatio;
      return;
    }

    const { x, y, scale } = this.metadata;

    const metadata = {
      startX: -x * this.canvasScale * IMAGE_QUALITY,
      startY: -y * this.canvasScale * IMAGE_QUALITY,
      scale: scale * this.canvasScale * IMAGE_QUALITY,
      orientation: 1
    };

    this.croppa.applyMetadata(metadata);
  }

  async cropImage() {
    this.$emit('crop-image-started');

    this.isImageLoaded = false;
    const { scale, startY, startX } = this.croppa.getMetadata();
    let croppedImageUrl;

    this.metadata = {
      ...this.metadata,
      w: this.computedCanvasWidth,
      h: this.computedCanvasHeight,
      x: -startX / IMAGE_QUALITY,
      y: -startY / IMAGE_QUALITY,
      scale: scale / IMAGE_QUALITY
    };

    if (!this.isGifAnimation) {
      const blob = await this.croppa.promisedBlob('image/png');
      const file = new File([blob], 'cropped-image.png', {
        lastModified: new Date().getTime(),
        type: 'image/png'
      });
      const { url } = await uploadCroppedFile(file, this.cropperImage, this.metadata, this.$store.getters['app/token']);

      croppedImageUrl = url;
    }

    this.$emit('crop-image-finished', {
      url: this.isGifAnimation ? this.cropperImage : croppedImageUrl,
      imageSize: this.imageSize,
      imageMetadata: this.metadata,
      canvasWidth: this.canvasWidth,
      canvasHeight: this.canvasHeight,
      widthInPercents: this.imageWrapperWidthInPercents,
      heightInPercents: this.imageWrapperHeightInPercents
    });
  }

  setInitImageSize() {
    if (this.isImageInitialized) {
      return;
    }
    if (this.croppa.naturalWidth < this.croppa.width && this.croppa.naturalHeight < this.croppa.height) {
      this.croppa.initialSize = 'natural';
      this.isImageInitialized = true;
      this.croppa.refresh();
    }
  }

  setCanvasSize() {
    this.canvasWidth = this.$refs.imageContainer.offsetWidth;
    this.canvasHeight = (this.$refs.imageContainer.offsetWidth / ONE_HUNDRED) * this.imageWrapperHeightInPercents;
  }

  public async updateImageSize(imageSize: ImageSize) {
    this.imageSize = imageSize;
  }

  setWidthInPercents(width: number) {
    this.imageWrapperWidthInPercents = (width / this.$refs.imageBlockContainer.offsetWidth) * ONE_HUNDRED;
  }

  setHeightInPercents() {
    this.imageWrapperHeightInPercents = (this.canvasHeight / this.canvasWidth) * ONE_HUNDRED;
  }

  private checkImageExtension(url: string) {
    if (url) {
      const match = url.match(/.+\/([^\\/]+\.([a-zA-Z]+))/);
      this.isGifAnimation = match ? match[TWO] === 'gif' : this.isGifAnimation;
    }
    this.$emit('is-gif-format', this.isGifAnimation);
  }
}
